/**
 * Compact, read-only field presentation for the Composer spec panel (task 703).
 *
 * The panel used to render its stage schemas with `SchemaBuilder` in read-only
 * mode, which reuses the schema *editor's* rows: a boxed card, a large icon and
 * generous padding, all designed for a full-width editing form. In a panel that
 * can be as narrow as a stacked mobile mount, those rows dominate the height and
 * wrap badly.
 *
 * These are presentational one-liners instead — a dot, the field name, its type.
 * They are used ONLY by `SpecPreview`; the schema editor's `FieldItem` in
 * `components/schema-builder/FieldList.tsx` is deliberately left untouched.
 */

/** One field as the panel needs it: what it is called, its type, is it required. */
export interface SpecField {
  name: string;
  type: string;
  required: boolean;
}

/**
 * Flatten a JSON-schema-ish object into the panel's field list.
 *
 * Tolerant by design: the composer spec is model-authored, so `properties` may
 * be missing, `required` may be absent, and a property may not declare a type.
 * Anything unreadable simply yields fewer fields rather than throwing.
 */
export function specFields(schema?: Record<string, unknown> | null): SpecField[] {
  const props = (schema as { properties?: Record<string, unknown> } | null)?.properties;
  if (!props || typeof props !== 'object') return [];
  const required = (schema as { required?: unknown })?.required;
  const requiredNames = new Set(Array.isArray(required) ? required.map(String) : []);

  return Object.entries(props).map(([name, raw]) => {
    const type = (raw as { type?: unknown } | null)?.type;
    return {
      name,
      type: typeof type === 'string' ? type : Array.isArray(type) ? type.join(' | ') : '',
      required: requiredNames.has(name),
    };
  });
}

/**
 * A single compact row: `● sleepHours   number`.
 *
 * A filled dot marks a required field, a hollow one an optional field — the same
 * distinction the schema editor draws with a "required" badge, at a fraction of
 * the width. The name truncates rather than wrapping so the type stays on the
 * same line at any panel width.
 */
export function SpecFieldRow({ field }: { field: SpecField }) {
  return (
    <div className="flex items-center gap-2 py-0.5 min-w-0" data-testid="spec-field-row">
      <span
        aria-hidden="true"
        className={
          field.required
            ? 'h-1.5 w-1.5 shrink-0 rounded-full bg-red-400 dark:bg-red-400'
            : 'h-1.5 w-1.5 shrink-0 rounded-full border border-gray-400 dark:border-gray-500'
        }
      />
      <span className="font-mono text-[11.5px] text-gray-800 dark:text-gray-200 truncate">
        {field.name}
      </span>
      {field.type && (
        <span className="shrink-0 text-[10.5px] text-blue-600 dark:text-blue-300">{field.type}</span>
      )}
      <span className="sr-only">{field.required ? 'required' : 'optional'}</span>
    </div>
  );
}

/** An eyebrow-labelled group of compact rows, e.g. "TAKES" over the field list. */
export function SpecFieldGroup({
  label,
  schema,
}: {
  label: string;
  schema?: Record<string, unknown> | null;
}) {
  const fields = specFields(schema);
  if (fields.length === 0) return null;
  return (
    <div className="min-w-0">
      <p className="mt-1.5 mb-0.5 text-[10.5px] uppercase tracking-[0.06em] text-gray-500 dark:text-gray-400">
        {label}
      </p>
      {fields.map((field) => (
        <SpecFieldRow key={field.name} field={field} />
      ))}
    </div>
  );
}
